import React, { useLayoutEffect, Suspense, lazy } from 'react';
import {
  Routes,
  Route,
  BrowserRouter as Router,
  useLocation
} from 'react-router-dom';

/** Private Routes */
import PrivateRoute from './PrivateRoute';

/** Public Routes */
import PublicRoute from './PublicRoute';
import HomePrivateRoute from './HomePrivateRoute';
import Unifit from 'Pages/Unifit/Unifit';
import Loader from 'Components/Loader/Loader';
import Disclaimer from 'Pages/Disclaimer/Disclaimer';
import InvestorInfo from 'Pages/InvestorInfo/investorInfo';
import ThankYou from 'Pages/ThankYou/ThankYou';
import Offers from 'Pages/Offers/Offers';

const BookALabVisitPagePackages = lazy(() =>
  import('Pages/BookALabVisit/Home')
);

const BookALabVisitPage = lazy(() =>
  import('Pages/BookALabVisit/BookALabVisitPage')
);

const Home = lazy(() => import('../Pages/Home/Home'));
const Login = lazy(() => import('../Pages/Login/Login'));
const CreateProfile = lazy(() =>
  import('../Pages/Login/Components/CreateProfile')
);
const PackagePage = lazy(() => import('../Pages/Packages/Home'));
const PackageDetailsPage = lazy(() => import('../Pages/PackagesDetails/Home'));
const TestsPage = lazy(() => import('../Pages/Tests/Home'));
const TestsDetailsPage = lazy(() => import('../Pages/TestsDetails/Home'));
const BookingPage = lazy(() => import('../Pages/Booking/Home'));
const WishList = lazy(() => import('Pages/WishList/Home'));
const SearchPage = lazy(() => import('Pages/SearchPage/Home'));
const BookingListing = lazy(() =>
  import('Pages/MyBooking/BookingListing/BookingListing')
);
const LocateLab = lazy(() => import('Pages/LocateLab/LocateLab'));
const BookingDetails = lazy(() =>
  import('Pages/MyBooking/BookingDetails/BookingDetails')
);
const Reschedule = lazy(() => import('Pages/MyBooking/Reschedule/Reschedule'));
const MyReports = lazy(() => import('Pages/MyBooking/MyReports/MyReports'));
const MyProfile = lazy(() => import('Pages/MyBooking/MyProfile/MyProfile'));
const AboutUnipath = lazy(() => import('Pages/AboutUnipath/AboutUnipath'));
const TestingSpeciality = lazy(() =>
  import('Pages/TestingSpeciality/TestingSpeciality')
);
const KnowledgeSharing = lazy(() =>
  import('Pages/KnowledgeSharing/KnowledgeSharing')
);
const TermsCondition = lazy(() =>
  import('Pages/TermsCondition/TermsCondition')
);
const PrivacyPolicy = lazy(() => import('Pages/PrivacyPolicy/PrivacyPolicy'));
const PaymentPolicy = lazy(() => import('Pages/PaymentPolicy/PaymentPolicy'));
const disclaimer = lazy(() => import('Pages/Disclaimer/Disclaimer'));
const Blogs = lazy(() => import('Pages/Blogs/Blogs'));
const BlogDetails = lazy(() => import('Pages/Blogs/BlogDetails'));
const CareerPage = lazy(() => import('Pages/Careers/Home'));
const CareerDetailsPage = lazy(() =>
  import('Pages/Careers/Components/ApplyNowForm')
);

const BookAHomeVisit = lazy(() => import('Pages/BookAHomeVisit/Home'));

const Payment = lazy(() => import('Pages/PaymentBooking/Payment'));
const PaymentResponse = lazy(() =>
  import('Pages/PaymentBooking/PaymentResponse')
);
const PaymentError = lazy(() => import('Pages/PaymentBooking/PaymentError'));

const ReachUs = lazy(() => import('Pages/ReachUs/ReachUs'));
const TestimonialPage = lazy(() => import('Pages/Testimonial/TestimonialPage'));
const WhyUnipath = lazy(() => import('Pages/WhyUnipath/WhyUnipath'));
const FAQPage = lazy(() => import('Pages/FAQPage/Home'));
const UnifitWeekendWellness = lazy(() =>
  import('Pages/UnifitWeekendWellness/Home')
);

const GraphRepotView = lazy(() => import('Pages/MyBooking/GraphView/Home'));
const NotFoundPage = lazy(() => import('Pages/NotFoundPage/NotFoundPage'));
/**
 * Scrolls to the top of page if any submission occurs or layout change occurs
 */
const Wrapper = ({ children }) => {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);
  return children;
};

const ProjectRoutes = () => {
  return (
    <Wrapper>
      <Suspense fallback={<Loader isLoading={true} />}>
        <Routes>
          <Route
            path="/"
            element={<Home />}
          />
          <Route
            path="/health-packages"
            element={<PackagePage />}
          />
          <Route
            path="/:city/health-packages"
            element={<PackagePage />}
          />
          <Route
            path="/:city/health-packages/category/:id"
            element={<PackagePage />}
          />
          <Route
            path="/health-packages/category/:id"
            element={<PackagePage />}
          />
          <Route
            path="/search/:value"
            element={<SearchPage />}
          />
          <Route
            path="/:city/blood-tests"
            element={<TestsPage />}
          />
          <Route
            path="/:city/blood-tests/category/:id"
            element={<TestsPage />}
          />
          <Route
            path="/blood-tests"
            element={<TestsPage />}
          />
          <Route
            path="/blood-tests/category/:id"
            element={<TestsPage />}
          />
          <Route
            path="/:city/health-packages/:id"
            element={<PackageDetailsPage />}
          />
          <Route
            path="/health-packages/:id"
            element={<PackageDetailsPage />}
          />
          <Route
            path="/thank-you"
            element={<ThankYou />}
          />

          <Route
            path="/investment-info"
            element={<InvestorInfo />}
          />

          <Route
            path="/:city/blood-tests/:id"
            element={<TestsDetailsPage />}
          />
          <Route
            path="/blood-tests/:id"
            element={<TestsDetailsPage />}
          />
          <Route
            path="/wishlist"
            element={
              <PrivateRoute>
                <WishList />
              </PrivateRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PublicRoute>
                <CreateProfile />
              </PublicRoute>
            }
          />

          <Route
            path="/my-booking"
            element={
              <HomePrivateRoute>
                <BookingListing />
              </HomePrivateRoute>
            }
          />
          <Route
            path="/my-booking/details/:id"
            element={
              <PrivateRoute>
                <BookingDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="/my-booking/details/reschedule/:id"
            element={
              <PrivateRoute>
                <Reschedule />
              </PrivateRoute>
            }
          />
          <Route
            path="/my-reports"
            element={
              <PrivateRoute>
                <MyReports />
              </PrivateRoute>
            }
          />
          <Route
            path="/my-reports/:id"
            element={
              <PrivateRoute>
                <GraphRepotView />
              </PrivateRoute>
            }
          />
          <Route
            path="/my-profile"
            element={
              <HomePrivateRoute>
                <MyProfile />
              </HomePrivateRoute>
            }
          />

          <Route
            path="/booking"
            element={
              <PrivateRoute>
                <BookingPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/:city/book-a-home-visit"
            element={<BookAHomeVisit />}
          />
          <Route
            path="/:city/book-a-home-visit/category/:id"
            element={<BookAHomeVisit />}
          />
          <Route
            path="/:city/book-a-lab-visit"
            element={<BookALabVisitPage />}
          />
          <Route
            path="/:city/book-a-lab-visit/packages"
            element={<BookALabVisitPagePackages />}
          />
          <Route
            path="/:city/book-a-lab-visit/packages/category/:id"
            element={<BookALabVisitPagePackages />}
          />

          <Route
            path="/:city/book-a-lab-visit/packages/:branch_code"
            element={<BookALabVisitPagePackages />}
          />
          <Route
            path="/:city/book-a-lab-visit/packages/:branch_code/category/:id"
            element={<BookALabVisitPagePackages />}
          />

          <Route
            path="/:city/unifit-weekend-wellness"
            element={<UnifitWeekendWellness />}
          />

          <Route
            path="/book-a-home-visit"
            element={<BookAHomeVisit />}
          />
          <Route
            path="/book-a-home-visit/category/:id"
            element={<BookAHomeVisit />}
          />

          <Route
            path="/book-a-lab-visit/packages/:branch_code/category/:id"
            element={<BookALabVisitPagePackages />}
          />
          <Route
            path="/book-a-lab-visit/packages/category/:id"
            element={<BookALabVisitPagePackages />}
          />
          <Route
            path="/book-a-lab-visit"
            element={<BookALabVisitPage />}
          />
          <Route
            path="/book-a-lab-visit/packages/:branch_code"
            element={<BookALabVisitPagePackages />}
          />
          <Route
            path="/book-a-lab-visit/packages"
            element={<BookALabVisitPagePackages />}
          />

          <Route
            path="/unifit-weekend-wellness"
            element={<UnifitWeekendWellness />}
          />

          {/* Payment Routes */}
          <Route
            path="/payment"
            element={<Payment />}
          />
          <Route
            path="/payment-error"
            element={<PaymentError />}
          />
          <Route
            path="/payment-response"
            element={<PaymentResponse />}
          />

          <Route
            path="/locatelab"
            element={<LocateLab />}
          />
          <Route
            path="/offers"
            element={<Offers />}
          />
          <Route
            path="/locatelab/:cityname"
            element={<LocateLab />}
          />

          <Route
            path="/terms-of-use"
            element={<TermsCondition />}
          />

          <Route
            path="/blogs"
            element={<Blogs />}
          />
          <Route
            path="/blogs/:id"
            element={<BlogDetails />}
          />

          <Route
            path="/why-unipath"
            element={<WhyUnipath />}
          />
          <Route
            path="/testimonials"
            element={<TestimonialPage />}
          />
          <Route
            path="/career"
            element={<CareerPage />}
          />
          <Route
            path="/career/:id"
            element={<CareerDetailsPage />}
          />
          <Route
            path="/about-us"
            element={<AboutUnipath />}
          />
          <Route
            path="/testing-specialty"
            element={<TestingSpeciality />}
          />
          <Route
            path="/knowledge-sharing"
            element={<KnowledgeSharing />}
          />
          <Route
            path="/frequently-asked-questions"
            element={<FAQPage />}
          />
          <Route
            path="/privacy-policy"
            element={<PrivacyPolicy />}
          />
          <Route
            path="/payment-policy"
            element={<PaymentPolicy />}
          />
          <Route
            path="/disclaimer"
            element={<Disclaimer />}
          />
          <Route
            path="/blogs"
            element={<Blogs />}
          />
          <Route
            path="/blogs/:id"
            element={<BlogDetails />}
          />
          <Route
            path="/reach-us"
            element={<ReachUs />}
          />

          <Route
            path="/unifit"
            element={<Unifit />}
          />

          <Route
            path="*"
            element={<NotFoundPage />}
          />
        </Routes>
      </Suspense>
    </Wrapper>
  );
};

export default ProjectRoutes; //withRouter HOC will let us use props for location and history
